import { useMemo, useState } from 'react';
import axios from 'axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, Minus, Package, Plus, ShoppingBag, ShoppingCart, Store } from 'lucide-react';
import { CatalogFormulaDto, CatalogItemDto, createOrder, fetchCatalog } from '../api/candidate';
import { Badge, Button, Callout, Card, EmptyState, cx } from '../components/charte';

// Commander (refonte du 14/10/2026) : même catalogue, même commande réglée en ligne. Lignes en cartes,
// quantités en pilule, panier collé en bas de page (au-dessus de la barre de navigation du téléphone).
// Les erreurs de commande s'affichent dans le panier au lieu d'une fenêtre d'alerte du navigateur.

const eur = (n: number) => n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

const UNAVAILABLE = 'Commande impossible pour le moment.';

function Stepper({ qty, onChange }: { qty: number; onChange: (n: number) => void }) {
  const button =
    'grid h-8 w-8 place-items-center rounded-full text-primary-600 transition-colors hover:bg-white disabled:opacity-35 disabled:hover:bg-transparent';
  return (
    <div className={cx('flex shrink-0 items-center gap-1 rounded-pill p-1', qty > 0 ? 'bg-primary-50' : 'bg-surface-2')}>
      <button type="button" onClick={() => onChange(Math.max(0, qty - 1))} className={button} disabled={qty === 0} aria-label="Retirer">
        <Minus size={15} />
      </button>
      <span className="w-6 text-center text-sm font-semibold text-heading">{qty}</span>
      <button type="button" onClick={() => onChange(qty + 1)} className={button} aria-label="Ajouter">
        <Plus size={15} />
      </button>
    </div>
  );
}

function Line({
  icon: Icon,
  title,
  description,
  meta,
  action,
}: {
  icon: typeof Package;
  title: string;
  description?: string | null;
  meta: string;
  action?: React.ReactNode;
}) {
  return (
    <li className="flex items-center gap-3 rounded-card bg-white p-4 shadow-card transition-shadow hover:shadow-card-hover">
      <span className="grid h-11 w-11 shrink-0 place-items-center self-start rounded-2xl bg-surface-indigo text-primary">
        <Icon size={18} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-heading">{title}</p>
        {description && <p className="mt-0.5 whitespace-pre-line text-xs leading-snug text-secondary">{description}</p>}
        <p className="mt-0.5 text-xs font-medium text-body">{meta}</p>
      </div>
      {action}
    </li>
  );
}

export default function CommandView() {
  const { data, isLoading, isError } = useQuery({ queryKey: ['candidate', 'catalog'], queryFn: fetchCatalog });

  // Quantités par identifiant (clé préfixée pour distinguer article/formule).
  const [qty, setQty] = useState<Record<string, number>>({});
  const [orderError, setOrderError] = useState<string | null>(null);

  const setItemQty = (key: string, n: number) => {
    setOrderError(null);
    setQty((prev) => ({ ...prev, [key]: n }));
  };

  const cartTotal = useMemo(() => {
    if (!data) return 0;
    let total = 0;
    for (const it of data.items) total += (qty[`item:${it.id}`] ?? 0) * it.priceInclTax;
    for (const f of data.formulas) total += (qty[`formula:${f.id}`] ?? 0) * f.priceInclTax;
    return total;
  }, [data, qty]);

  const lineCount = Object.values(qty).filter((n) => n > 0).length;

  const orderMutation = useMutation({
    mutationFn: () => {
      if (!data) throw new Error('Catalogue indisponible.');
      const items = data.items
        .map((it) => ({ catalogItemId: it.id, quantity: qty[`item:${it.id}`] ?? 0 }))
        .filter((l) => l.quantity > 0);
      const formulas = data.formulas
        .map((f) => ({ catalogFormulaId: f.id, quantity: qty[`formula:${f.id}`] ?? 0 }))
        .filter((l) => l.quantity > 0);
      return createOrder({ items, formulas });
    },
    onSuccess: (res) => {
      // La commande se règle tout de suite : la facture n'est émise qu'une fois payée.
      if (res.checkoutUrl) window.location.href = res.checkoutUrl;
      else setOrderError(UNAVAILABLE);
    },
    onError: (e: unknown) => {
      const message = axios.isAxiosError(e) ? e.response?.data?.message : null;
      setOrderError(typeof message === 'string' ? message : UNAVAILABLE);
    },
  });

  const header = (subtitle: string) => (
    <div>
      <h1 className="text-title-md text-heading sm:text-title-lg">Commander</h1>
      <p className="mt-1 text-sm text-secondary">{subtitle}</p>
    </div>
  );

  if (isLoading) {
    return (
      <div className="grid gap-5 sm:gap-6">
        {header('Les prestations proposées par votre auto-école.')}
        <Card className="flex items-center text-sm text-secondary">
          <Loader2 className="mr-2 animate-spin" size={16} /> Chargement du catalogue…
        </Card>
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="grid gap-5 sm:gap-6">
        {header('Les prestations proposées par votre auto-école.')}
        <p role="alert" className="rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
          Impossible de charger le catalogue.
        </p>
      </div>
    );
  }

  const hasCatalog = data.items.length > 0 || data.formulas.length > 0;
  // Sans paiement en ligne, le catalogue se consulte mais rien ne se commande. Une commande préparée
  // par l'auto-école se règle sur sa facture : la racheter ici la ferait payer et compter deux fois.
  const pendingOrders = data.pendingOrders ?? [];
  const canOrder = data.paymentEnabled && pendingOrders.length === 0;

  return (
    <section className="grid gap-5 sm:gap-6">
      {header(
        data.paymentEnabled && pendingOrders.length === 0
          ? 'Choisissez vos prestations et réglez-les en ligne : votre facture vous est envoyée dès le paiement.'
          : 'Les prestations proposées par votre auto-école.',
      )}

      {pendingOrders.length > 0 && (
        <div role="status">
          <Callout tone="info">
          Votre auto-école vous a déjà préparé {pendingOrders.length > 1 ? 'des commandes' : 'une commande'} :{' '}
          <strong>{pendingOrders.map((o) => `${o.title} (${o.reference})`).join(', ')}</strong>. Elle vous sera facturée et
          vous la réglerez depuis « Mes paiements » : inutile de la commander ici. Pour ajouter autre chose, contactez votre
          auto-école.
          </Callout>
        </div>
      )}

      {hasCatalog && !data.paymentEnabled && (
        <div role="status" className="rounded-row bg-warn-soft px-4 py-3 text-sm leading-relaxed text-warn-fg">
          La commande en ligne n'est pas disponible : votre auto-école n'a pas encore activé le paiement en ligne.
          Adressez-vous à elle pour commander ces prestations.
        </div>
      )}

      {!hasCatalog && (
        <Card>
          <EmptyState icon={Store} title="Catalogue vide" description="Le catalogue de votre auto-école n'est pas encore disponible." />
        </Card>
      )}

      {data.formulas.length > 0 && (
        <div className="grid gap-3">
          <h2 className="px-1 text-title-sm text-heading">Formules</h2>
          <ul className="grid gap-3 lg:grid-cols-2">
            {data.formulas.map((f: CatalogFormulaDto) => (
              <Line
                key={f.id}
                icon={Package}
                title={f.name}
                meta={`${eur(f.priceInclTax)} TTC · ${f.itemsCount} article(s)`}
                action={canOrder && <Stepper qty={qty[`formula:${f.id}`] ?? 0} onChange={(n) => setItemQty(`formula:${f.id}`, n)} />}
              />
            ))}
          </ul>
        </div>
      )}

      {data.items.length > 0 && (
        <div className="grid gap-3">
          <h2 className="px-1 text-title-sm text-heading">Articles</h2>
          <ul className="grid gap-3 lg:grid-cols-2">
            {data.items.map((it: CatalogItemDto) => (
              <Line
                key={it.id}
                icon={ShoppingBag}
                title={it.name}
                description={it.description}
                meta={`${eur(it.priceInclTax)} TTC${it.hours > 0 ? ` · ${it.hours} h` : ''}`}
                action={canOrder && <Stepper qty={qty[`item:${it.id}`] ?? 0} onChange={(n) => setItemQty(`item:${it.id}`, n)} />}
              />
            ))}
          </ul>
        </div>
      )}

      {/* Panier : collé en bas de page, au-dessus de la barre de navigation du téléphone. */}
      {canOrder && lineCount > 0 && (
        <div className="bottom-above-nav sticky z-30">
          <Card className="!p-4 shadow-pop">
            <div className="flex items-center gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-secondary">
                  <Badge tone="todo" className="mr-1.5">
                    {lineCount}
                  </Badge>
                  ligne{lineCount > 1 ? 's' : ''}
                </p>
                <p className="mt-0.5 text-lg font-semibold text-heading">{eur(cartTotal)}</p>
              </div>
              <Button variant="primary" icon={ShoppingCart} loading={orderMutation.isPending} onClick={() => orderMutation.mutate()}>
                {orderMutation.isPending ? 'Redirection…' : 'Payer ma commande'}
              </Button>
            </div>
            {orderError && (
              <p role="alert" className="mt-3 rounded-xl bg-danger-light px-3 py-2 text-sm text-danger-dark">
                {orderError}
              </p>
            )}
          </Card>
        </div>
      )}
    </section>
  );
}
